import React, { useState, useEffect } from "react";
import { IEvent, IShow, IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";
import AwardDetailsModal, { IAwardDetails } from "../AwardDetailsModal/AwardDetailsModal";

import "./AkcObedienceAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: IScore[]; // Optional: if provided, use these instead of fetching
}

interface IAward {
  registered_name: string;
  breed: string;
  score: number;
  arm_number: string;
}

interface ICombinedAward extends IAward {
  combined_score: number;
  open_b_score: number;
  utility_b_score: number;
}

interface IAwardsData {
  regular: {
    highInTrial?: IAward;
    highInTrialDetails?: IAwardDetails[];
    highRegular?: IAward;
    highRegularDetails?: IAwardDetails[];
    highPreferred?: IAward;
    highPreferredDetails?: IAwardDetails[];
    highCombined?: ICombinedAward;
    highCombinedDetails?: IAwardDetails[];
  };
  specialty?: {
    highInTrial?: IAward;
    highInTrialDetails?: IAwardDetails[];
    highRegular?: IAward;
    highRegularDetails?: IAwardDetails[];
    highPreferred?: IAward;
    highPreferredDetails?: IAwardDetails[];
    highCombined?: ICombinedAward;
    highCombinedDetails?: IAwardDetails[];
  };
}

const AkcObedienceAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IAwardsData | null>(null);
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    details: IAwardDetails[];
    title: string;
  }>({
    isOpen: false,
    details: [],
    title: '',
  });

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      
      // Use provided scores or fetch them
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      // Filter scores for this show
      const showScores = scores.filter(
        (s) =>
          s.show_type === Constants.show_type.obedience &&
          s.show_id.toString() === show._id.toString() &&
          s.obedience?.result_type === Constants.score_result_type.qualified
      );

      const calculatedAwards = calculateAwards(showScores);
      setAwards(calculatedAwards);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateAwards = (scores: IScore[]): IAwardsData => {
    // Regular classes levels
    const regularLevels = [
      Constants.dog_class_element_level.novice_a,
      Constants.dog_class_element_level.novice_b,
      Constants.dog_class_element_level.open_a,
      Constants.dog_class_element_level.open_b,
      Constants.dog_class_element_level.utility_a,
      Constants.dog_class_element_level.utility_b,
    ];

    // Preferred classes levels
    const preferredLevels = [
      Constants.dog_class_element_level.preferred_novice,
      Constants.dog_class_element_level.preferred_open,
      Constants.dog_class_element_level.preferred_utility,
    ];

    // Calculate regular awards
    const regularAwards = calculateAwardsForClasses(scores, regularLevels, preferredLevels, null, show);

    // Calculate specialty awards if applicable
    let specialtyAwards;
    if (show.specialty_breed && show.specialty_breed.length > 0) {
      const specialtyScores = scores.filter((s) => s.breed === show.specialty_breed);
      specialtyAwards = calculateAwardsForClasses(specialtyScores, regularLevels, preferredLevels, show.specialty_breed, show);
    }

    return {
      regular: regularAwards,
      specialty: specialtyAwards,
    };
  };

  const calculateAwardsForClasses = (
    scores: IScore[],
    regularLevels: string[],
    preferredLevels: string[],
    breedFilter: string | null,
    show: IShow
  ) => {
    // Calculate High in Trial (HIT)
    // HIT is the highest qualifying score among ALL entries (must be >= 170)
    // Each dog should only appear once with their HIGHEST score
    const dogHighScores = new Map<string, {
      score: number;
      registered_name: string;
      breed: string;
      arm_number: string;
      level: string;
    }>();
    
    scores.forEach((s) => {
      const dogId = s.dog_id.toString();
      const deductions = s.obedience?.total_deductions_only
        ? s.obedience?.total_deductions ?? 0
        : (s.obedience?.element_deductions || []).reduce(
            (prev, current) =>
              prev +
              (isNaN(parseFloat(current.element_deductions))
                ? 0
                : parseFloat(current.element_deductions)),
            0
          );
      const score = 200 - deductions;
      
      // Must be qualifying score (>= 170)
      if (score >= 170) {
        const existing = dogHighScores.get(dogId);
        if (!existing || score > existing.score) {
          dogHighScores.set(dogId, {
            score,
            registered_name: s.registered_name || "",
            breed: s.breed || "",
            arm_number: s.arm_number || "",
            level: s.dog_class?.level || "",
          });
        }
      }
    });

    // Convert to array and sort
    const highInTrialCandidates: IAwardDetails[] = Array.from(dogHighScores.values()).map(dog => ({
      registered_name: dog.registered_name,
      breed: dog.breed,
      arm_number: dog.arm_number,
      level: dog.level,
      advancedBScore: dog.score, // Reusing field for the score
      total: dog.score,
      totalTime: 0, // Not used for Obedience HIT
    }));

    // Sort by score (desc), then by armband number (asc) as tiebreaker
    highInTrialCandidates.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      return parseInt(a.arm_number || "999") - parseInt(b.arm_number || "999");
    });

    const highInTrial = highInTrialCandidates.length > 0 ? {
      registered_name: highInTrialCandidates[0].registered_name,
      breed: highInTrialCandidates[0].breed,
      score: highInTrialCandidates[0].total,
      arm_number: highInTrialCandidates[0].arm_number,
    } : undefined;

    // Group scores by dog and level for other awards
    const dogScores = new Map<string, Record<string, any>>();

    scores.forEach((s) => {
      const dogId = s.dog_id.toString();
      const level = s.dog_class?.level || "";
      const deductions = s.obedience?.total_deductions_only
        ? s.obedience?.total_deductions ?? 0
        : (s.obedience?.element_deductions || []).reduce(
            (prev, current) =>
              prev +
              (isNaN(parseFloat(current.element_deductions))
                ? 0
                : parseFloat(current.element_deductions)),
            0
          );
      const score = 200 - deductions;

      if (!dogScores.has(dogId)) {
        dogScores.set(dogId, {
          registered_name: s.registered_name || "",
          breed: s.breed || "",
          arm_number: s.arm_number || "",
        });
      }

      dogScores.get(dogId)![level] = score;
    });

    // Calculate High Score in Regular Classes - top dog from each class
    const highRegularCandidates: IAwardDetails[] = [];
    
    // Group scores by level (class)
    const regularScoresByLevel = new Map<string, IScore[]>();
    scores.forEach((s) => {
      const level = s.dog_class?.level || "";
      if (regularLevels.includes(level)) {
        if (!regularScoresByLevel.has(level)) {
          regularScoresByLevel.set(level, []);
        }
        regularScoresByLevel.get(level)!.push(s);
      }
    });

    // For each class, find the top scorer
    regularScoresByLevel.forEach((classScores, level) => {
      // Calculate final scores for this class
      const scoresWithFinal = classScores.map((s) => {
        const deductions = s.obedience?.total_deductions_only
          ? s.obedience?.total_deductions ?? 0
          : (s.obedience?.element_deductions || []).reduce(
              (prev, current) =>
                prev +
                (isNaN(parseFloat(current.element_deductions))
                  ? 0
                  : parseFloat(current.element_deductions)),
              0
            );
        const finalScore = 200 - deductions;
        return { score: s, finalScore };
      });

      // Sort by score (desc), then by armband
      scoresWithFinal.sort((a, b) => {
        if (a.finalScore !== b.finalScore) return b.finalScore - a.finalScore;
        return parseInt(a.score.arm_number || "999") - parseInt(b.score.arm_number || "999");
      });

      if (scoresWithFinal.length > 0) {
        const topScore = scoresWithFinal[0].finalScore;
        
        // Check if there's a tie
        const tiedScores = scoresWithFinal.filter(sf => sf.finalScore === topScore);
        
        let winner = tiedScores[0].score;
        
        // If there's a tie, check run_offs to see if there's a resolved winner
        if (tiedScores.length > 1 && show.run_offs && show.run_offs.length > 0) {
          const tiedArmNumbers = tiedScores.map(ts => ts.score.arm_number);
          
          // Find a run-off that matches this class and these tied dogs
          const matchingRunOff = show.run_offs.find(ro => 
            ro.show_element === level &&
            ro.winner_arm_number &&
            tiedArmNumbers.includes(ro.winner_arm_number)
          );
          
          if (matchingRunOff && matchingRunOff.winner_arm_number) {
            // Use the run-off winner
            const winnerScore = tiedScores.find(ts => ts.score.arm_number === matchingRunOff.winner_arm_number);
            if (winnerScore) {
              winner = winnerScore.score;
            }
          }
        }

        highRegularCandidates.push({
          registered_name: winner.registered_name || "",
          breed: winner.breed || "",
          arm_number: winner.arm_number || "",
          level: level,
          advancedBScore: topScore,
          total: topScore,
          totalTime: 0,
        });
      }
    });

    // Sort candidates by score (desc) to determine overall winner
    highRegularCandidates.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      return parseInt(a.arm_number || "999") - parseInt(b.arm_number || "999");
    });

    const highRegular = highRegularCandidates.length > 0 ? {
      registered_name: highRegularCandidates[0].registered_name,
      breed: highRegularCandidates[0].breed,
      score: highRegularCandidates[0].total,
      arm_number: highRegularCandidates[0].arm_number,
    } : undefined;

    // Calculate High Score in Preferred Classes - top dog from each class
    const highPreferredCandidates: IAwardDetails[] = [];
    
    // Group scores by level (class)
    const preferredScoresByLevel = new Map<string, IScore[]>();
    scores.forEach((s) => {
      const level = s.dog_class?.level || "";
      if (preferredLevels.includes(level)) {
        if (!preferredScoresByLevel.has(level)) {
          preferredScoresByLevel.set(level, []);
        }
        preferredScoresByLevel.get(level)!.push(s);
      }
    });

    // For each class, find the top scorer
    preferredScoresByLevel.forEach((classScores, level) => {
      // Calculate final scores for this class
      const scoresWithFinal = classScores.map((s) => {
        const deductions = s.obedience?.total_deductions_only
          ? s.obedience?.total_deductions ?? 0
          : (s.obedience?.element_deductions || []).reduce(
              (prev, current) =>
                prev +
                (isNaN(parseFloat(current.element_deductions))
                  ? 0
                  : parseFloat(current.element_deductions)),
              0
            );
        const finalScore = 200 - deductions;
        return { score: s, finalScore };
      });

      // Sort by score (desc), then by armband
      scoresWithFinal.sort((a, b) => {
        if (a.finalScore !== b.finalScore) return b.finalScore - a.finalScore;
        return parseInt(a.score.arm_number || "999") - parseInt(b.score.arm_number || "999");
      });

      if (scoresWithFinal.length > 0) {
        const topScore = scoresWithFinal[0].finalScore;
        
        // Check if there's a tie
        const tiedScores = scoresWithFinal.filter(sf => sf.finalScore === topScore);
        
        let winner = tiedScores[0].score;
        
        // If there's a tie, check run_offs to see if there's a resolved winner
        if (tiedScores.length > 1 && show.run_offs && show.run_offs.length > 0) {
          const tiedArmNumbers = tiedScores.map(ts => ts.score.arm_number);
          
          // Find a run-off that matches this class and these tied dogs
          const matchingRunOff = show.run_offs.find(ro => 
            ro.show_element === level &&
            ro.winner_arm_number &&
            tiedArmNumbers.includes(ro.winner_arm_number)
          );
          
          if (matchingRunOff && matchingRunOff.winner_arm_number) {
            // Use the run-off winner
            const winnerScore = tiedScores.find(ts => ts.score.arm_number === matchingRunOff.winner_arm_number);
            if (winnerScore) {
              winner = winnerScore.score;
            }
          }
        }

        highPreferredCandidates.push({
          registered_name: winner.registered_name || "",
          breed: winner.breed || "",
          arm_number: winner.arm_number || "",
          level: level,
          advancedBScore: topScore,
          total: topScore,
          totalTime: 0,
        });
      }
    });

    // Sort candidates by score (desc) to determine overall winner
    highPreferredCandidates.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      return parseInt(a.arm_number || "999") - parseInt(b.arm_number || "999");
    });

    const highPreferred = highPreferredCandidates.length > 0 ? {
      registered_name: highPreferredCandidates[0].registered_name,
      breed: highPreferredCandidates[0].breed,
      score: highPreferredCandidates[0].total,
      arm_number: highPreferredCandidates[0].arm_number,
    } : undefined;

    // Calculate High Combined (Open B + Utility B) - collect all candidates
    const highCombinedCandidates: IAwardDetails[] = [];
    dogScores.forEach((levels, dogId) => {
      const openBScore = levels[Constants.dog_class_element_level.open_b];
      const utilityBScore = levels[Constants.dog_class_element_level.utility_b];

      // Both Open B and Utility B scores must exist
      if (typeof openBScore === "number" && typeof utilityBScore === "number") {
        const combinedScore = openBScore + utilityBScore;
        
        highCombinedCandidates.push({
          registered_name: levels.registered_name,
          breed: levels.breed,
          arm_number: levels.arm_number,
          advancedBScore: openBScore, // Reusing field for Open B
          excellentBScore: utilityBScore, // Reusing field for Utility B
          total: combinedScore,
          totalTime: 0, // Not used for Obedience
        });
      }
    });

    // Sort by total (desc)
    highCombinedCandidates.sort((a, b) => b.total - a.total);

    const highCombined = highCombinedCandidates.length > 0 ? {
      registered_name: highCombinedCandidates[0].registered_name,
      breed: highCombinedCandidates[0].breed,
      score: highCombinedCandidates[0].total,
      combined_score: highCombinedCandidates[0].total,
      open_b_score: highCombinedCandidates[0].advancedBScore!,
      utility_b_score: highCombinedCandidates[0].excellentBScore!,
      arm_number: highCombinedCandidates[0].arm_number,
    } : undefined;

    return {
      highInTrial,
      highInTrialDetails: highInTrialCandidates,
      highRegular,
      highRegularDetails: highRegularCandidates,
      highPreferred,
      highPreferredDetails: highPreferredCandidates,
      highCombined,
      highCombinedDetails: highCombinedCandidates,
    };
  };

  const openDetailsModal = (title: string, details: IAwardDetails[]) => {
    setDetailsModal({
      isOpen: true,
      details,
      title,
    });
  };

  const closeDetailsModal = () => {
    setDetailsModal({
      isOpen: false,
      details: [],
      title: '',
    });
  };

  const renderAwardSection = (title: string, award: IAward | ICombinedAward | undefined, isCombined: boolean = false, details?: IAwardDetails[]) => {
    return (
      <div className="awardSection">
        <h4>{title}</h4>
        {award ? (
          <>
            <p className="awardWinner">
              #{award.arm_number} {award.registered_name} ({award.breed}) –{" "}
              {isCombined
                ? `Combined Score ${(award as ICombinedAward).combined_score} (Open B: ${(award as ICombinedAward).open_b_score}, Utility B: ${(award as ICombinedAward).utility_b_score})`
                : `Score ${award.score}`}
            </p>
            {details && details.length > 0 && (
              <button className="viewDetailsLink" onClick={() => openDetailsModal(title, details)}>
                View Details
              </button>
            )}
          </>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AkcObedienceAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="AkcObedienceAwards error">Failed to calculate awards</div>;
  }

  return (
    <>
      <div className="AkcObedienceAwards">
        <div className="regularAwards">
          {renderAwardSection("High in Trial", awards.regular.highInTrial, false, awards.regular.highInTrialDetails)}
          {renderAwardSection("High Score in Regular Classes", awards.regular.highRegular, false, awards.regular.highRegularDetails)}
          {renderAwardSection("High Score in Preferred Classes", awards.regular.highPreferred, false, awards.regular.highPreferredDetails)}
          {renderAwardSection("High Combined (Open B + Utility B)", awards.regular.highCombined, true, awards.regular.highCombinedDetails)}
        </div>

        {awards.specialty && show.specialty_breed && (
          <div className="specialtyAwards">
            <div className="specialtyDivider"></div>
            {renderAwardSection(`(${show.specialty_breed}) High in Trial`, awards.specialty.highInTrial, false, awards.specialty.highInTrialDetails)}
            {renderAwardSection(`(${show.specialty_breed}) High Score in Regular Classes`, awards.specialty.highRegular, false, awards.specialty.highRegularDetails)}
            {renderAwardSection(`(${show.specialty_breed}) High Score in Preferred Classes`, awards.specialty.highPreferred, false, awards.specialty.highPreferredDetails)}
            {renderAwardSection(`(${show.specialty_breed}) High Combined (Open B + Utility B)`, awards.specialty.highCombined, true, awards.specialty.highCombinedDetails)}
          </div>
        )}
      </div>
      
      <AwardDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={closeDetailsModal}
        awardTitle={detailsModal.title}
        showName={show.custom_show_name || show.show_name || ""}
        showType={(Constants.show_type as any)[show.show_type] || show.show_type}
        showDate={event.start_date ? new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ""}
        details={detailsModal.details}
        awardType='highCombined'
      />
    </>
  );
};

export default AkcObedienceAwards;